// ./src/components/features/EmailLayouts/EmailLayoutSelector/EmailLayoutSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { useEmailLayouts } from "@/hooks/useEmailLayouts";
import { LoadingIndicator } from "@/components/generic";
import { EmailLayout, isSystemLayout } from "@/utils/api";
import { useApp } from "@/contexts";
import { STATUSES } from "@sdflc/utils";

export interface EmailLayoutSelectorProps {
  /** Selected email layout ID */
  value: string | null | undefined;

  /** Callback when email layout selection changes */
  onChange?: (emailLayoutId: string | null, emailLayout?: EmailLayout) => void;

  /** Label for the select field */
  label?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Description text below the field */
  description?: string;

  /** Whether to include inactive layouts */
  includeInactive?: boolean;

  /** Custom filter function for layouts */
  layoutFilter?: (layout: EmailLayout) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select */
  [key: string]: any;
}

export const EmailLayoutSelector: React.FC<EmailLayoutSelectorProps> = ({
  value,
  onChange,
  label = "Email Layout",
  placeholder = "Select an email layout",
  description,
  includeInactive = false,
  layoutFilter,
  required = false,
  loadingPlaceholder = "Loading layouts...",
  emptyText = "No layouts available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { user } = useApp();
  const userId = user?.id;

  const { emailLayouts, loading, isEmpty, lastError } = useEmailLayouts({
    autoLoad: true,
    userId,
    initialFilters: includeInactive ? {} : { status: [STATUSES.ACTIVE] },
  });

  // Filter and prepare layout options
  const layoutOptions = useMemo(() => {
    let filteredLayouts = emailLayouts;

    // Apply custom filter if provided
    if (layoutFilter) {
      filteredLayouts = filteredLayouts.filter(layoutFilter);
    }

    // Sort: user layouts first, then system layouts, alphabetically within each group
    filteredLayouts.sort((a, b) => {
      const aIsSystem = isSystemLayout(a);
      const bIsSystem = isSystemLayout(b);

      // User layouts come first
      if (aIsSystem !== bIsSystem) {
        return aIsSystem ? 1 : -1;
      }

      // Alphabetically within each group
      const nameA = a.name || "Unnamed Layout";
      const nameB = b.name || "Unnamed Layout";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredLayouts.map((layout) => ({
      value: layout.id || "",
      label: isSystemLayout(layout)
        ? `${layout.name || "Unnamed Layout"} (System)`
        : layout.name || "Unnamed Layout",
      disabled: layout.status !== STATUSES.ACTIVE,
    }));
  }, [emailLayouts, includeInactive, layoutFilter]);

  // Create a map for quick layout lookup by ID
  const layoutMap = useMemo(() => {
    const map = new Map<string, EmailLayout>();
    emailLayouts.forEach((layout) => {
      if (layout.id) {
        map.set(layout.id, layout);
      }
    });
    return map;
  }, [emailLayouts]);

  // Auto-select first layout if required and no value provided
  useEffect(() => {
    if (
      required &&
      !value &&
      layoutOptions.length > 0 &&
      !loading &&
      onChange
    ) {
      const firstLayout = layoutOptions[0];
      if (firstLayout && !firstLayout.disabled) {
        const layoutObject = layoutMap.get(firstLayout.value);
        onChange(firstLayout.value, layoutObject);
      }
    }
  }, [required, value, layoutOptions.length, loading]);

  // Handle onChange with layout object lookup
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedLayout = layoutMap.get(selectedValue);
      onChange?.(selectedValue, selectedLayout);
    } else {
      onChange?.(null, undefined);
    }
  };

  // Determine select state
  const isInitialLoading = loading && emailLayouts.length === 0;
  const hasError = lastError && !loading;
  const isEmptyState = isEmpty && !loading;

  const selectState = {
    data: isInitialLoading || hasError || isEmptyState ? [] : layoutOptions,
    placeholder: isInitialLoading
      ? loadingPlaceholder
      : hasError
      ? "Error loading layouts"
      : isEmptyState
      ? emptyText
      : placeholder,
    disabled: disabled || isInitialLoading || hasError || isEmptyState,
    error: hasError ? error || "Failed to load layouts" : error,
    rightSection:
      isInitialLoading || (loading && emailLayouts.length > 0) ? (
        <LoadingIndicator size={size} color="gray" />
      ) : (
        selectProps.rightSection
      ),
  };

  return (
    <Select
      {...selectProps}
      label={label}
      description={description}
      size={size}
      data={selectState.data}
      value={value ?? null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error}
      rightSection={selectState.rightSection}
    />
  );
};
